import React from 'react';
import { APICONSTANTS, CONSTANTS } from 'src/components/Constants';
import AutocompleteInput from 'src/components/UI/input/AutocompleteInput';
import { Grid } from '@mui/material';
import { SnackContext } from 'src/store/ContextStore';
import { getColor } from 'src/util/util';
import { renderTagChips } from './tagChips';
import AnonymousFreeTextBanner from '../../AnonymousFreeTextBanner';
import axios from 'src/axios';

// Person-level tags: labels on an individual member (the selected node), stored
// on the working models (UserChanged for the proband, FamilyChanged for other
// members). Mirrors the family-level Tags.jsx and shares the org palette
// (Client.clientTags) fetched via GET /pedigree/tags.

const isSystemTag = (tag) => {
  const lower = (tag?.label ?? tag)?.toLowerCase() ?? '';
  return lower.startsWith('imported from') || lower.includes('merge');
};

function PersonTags({ data, user, onDirtyChange, saveRef, discardRef }) {
  const { setSnack } = React.useContext(SnackContext);

  const familyMemberId = data?.familyMemberId ?? data?.proBandId;
  const isProband = !!data?.proBandId;
  const user_id = user?.user_id || user?._id;

  const initialTags = React.useMemo(
    () =>
      (data?.personTags &&
        data.personTags.map((t) => t.tag).filter((t) => !isSystemTag(t))) ||
      [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [tags, setTags] = React.useState(initialTags);

  const [options, setOptions] = React.useState(
    CONSTANTS.tags.filter((t) => !isSystemTag(t))
  );

  React.useEffect(() => {
    axios.get(APICONSTANTS.setTags).then(({ data: { clientTags } }) => {
      if (clientTags.length > 0) {
        setOptions(clientTags.filter((t) => !isSystemTag(t)));
      }
    });
  }, []);

  const handleTags = (value) => {
    const next = [];
    value.map((tag) => {
      if (typeof tag === 'string' && !next.includes(tag)) next.push(tag);
      else {
        const label = tag?.inputValue || tag?.label;
        label && !next.includes(label) && next.push(label);
      }
    });
    setTags(next);
    onDirtyChange?.(true);
  };

  const onSave = async () => {
    const clientTags = [];
    const personTags = [];
    tags.forEach((tag) => {
      const tagColor =
        options.find((t) => t.label === tag)?.tagColor ?? getColor(tag);
      clientTags.push({ label: tag, tagColor });
      personTags.push({ tag, tagColor });
    });

    await axios
      .post(APICONSTANTS.personTags, {
        user_id,
        familyMemberId,
        isProband,
        clientTags,
        personTags
      })
      .then(() => {
        onDirtyChange?.(false);
        setSnack({ status: true, msg: 'Tags Updated', severity: 'success' });
      })
      .catch(() => {
        setSnack({ status: true, msg: 'An error occured', severity: 'error' });
      });
  };

  const onDiscard = () => {
    setTags(initialTags);
    onDirtyChange?.(false);
  };

  React.useEffect(() => {
    if (saveRef) saveRef.current = onSave;
    if (discardRef) discardRef.current = onDiscard;
  });

  return (
    <>
      <AnonymousFreeTextBanner sx={{ mb: 1.5 }} />
      <Grid container spacing={2}>
        <Grid size={12}>
          <AutocompleteInput
            multiple
            options={options}
            getOptionLabel={(option) => option}
            value={tags}
            onChange={(v) => handleTags(v)}
            renderValue={renderTagChips(options)}
          />
        </Grid>
      </Grid>
    </>
  );
}

export default PersonTags;
